import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { PayoutModal } from "@/components/earnings/PayoutModal";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft, Banknote, TrendingUp, Clock, CheckCircle } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export default function Earnings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [balance, setBalance] = useState({ available: 0, pending: 0, total: 0 });
  const [lastPayout, setLastPayout] = useState<any>(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEarningsData();
  }, []);

  const loadEarningsData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load ONLY affiliate commission transactions
      const { data: txData, error: txError } = await supabase
        .from("earnings_ledger")
        .select("*")
        .eq("user_id", user.id)
        .in("transaction_type", ['affiliate_commission', 'payout'])
        .order("created_at", { ascending: false });

      if (txError) throw txError;
      setTransactions(txData || []);

      // Calculate balance from affiliate commissions only
      const totalEarned = txData
        ?.filter(tx => tx.transaction_type === 'affiliate_commission')
        .reduce((sum, tx) => sum + parseFloat(tx.amount.toString()), 0) || 0;

      const totalPaidOut = txData
        ?.filter(tx => tx.transaction_type === 'payout')
        .reduce((sum, tx) => sum + Math.abs(parseFloat(tx.amount.toString())), 0) || 0;

      // Get pending payouts (only bank type - affiliate payouts)
      const { data: pendingPayouts } = await supabase
        .from("payouts")
        .select("amount")
        .eq("user_id", user.id)
        .eq("payout_type", "bank")
        .in("status", ['pending', 'processing']);

      const pendingAmount = pendingPayouts?.reduce((sum, p) => sum + parseFloat(p.amount.toString()), 0) || 0;

      setBalance({
        available: totalEarned - totalPaidOut - pendingAmount,
        pending: pendingAmount,
        total: totalEarned
      });

      // Get last completed payout
      const { data: lastPayoutData } = await supabase
        .from("payouts")
        .select("*")
        .eq("user_id", user.id)
        .eq("payout_type", "bank")
        .eq("status", "completed")
        .order("processed_at", { ascending: false })
        .limit(1)
        .single();

      setLastPayout(lastPayoutData);
    } catch (error) {
      console.error("Error loading earnings:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionTypeBadge = (type: string) => {
    const typeConfig: Record<string, { className: string; label: string }> = {
      affiliate_commission: { 
        className: "bg-primary text-primary-foreground", 
        label: t("earnings.transactionTypes.affiliate_commission") 
      },
      payout: { 
        className: "bg-destructive text-destructive-foreground", 
        label: t("earnings.transactionTypes.payout") 
      }
    };

    const config = typeConfig[type] || { className: "", label: type };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-surface">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <p className="font-heading text-xl">{t("common.loading")}...</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8 max-w-7xl">
        {/* Breadcrumb Navigation */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/dashboard">
                {t("common.dashboard")}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("earnings.affiliateTitle")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Back Button + Title */}
        <div className="flex items-center gap-4 mb-2">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("common.back")}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          
          <h1 className="font-heading text-3xl font-bold text-dark">
            {t("earnings.affiliateTitle")}
          </h1>
        </div>
        
        <p className="text-muted-foreground mb-8 ml-14">
          {t("earnings.affiliateSubtitle")}
        </p>

        {/* Info Box */}
        <BrutalCard className="mb-6 p-6 bg-primary/10 border-primary">
          <div className="flex items-start gap-3">
            <Banknote className="w-6 h-6 text-primary mt-0.5" />
            <div>
              <h3 className="font-bold mb-1">{t("earnings.howItWorks")}</h3>
              <p className="text-sm text-muted-foreground">
                {t("earnings.affiliateExplanation")}
              </p>
            </div>
          </div>
        </BrutalCard>

        {/* Balance Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <BrutalCard className="p-6">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-success" />
              <div className="text-sm text-muted-foreground">{t("earnings.available")}</div>
            </div>
            <div className="text-3xl font-bold text-success">€{balance.available.toFixed(2)}</div>
          </BrutalCard>

          <BrutalCard className="p-6">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-warning" />
              <div className="text-sm text-muted-foreground">{t("earnings.pending")}</div>
            </div>
            <div className="text-3xl font-bold text-warning">€{balance.pending.toFixed(2)}</div>
          </BrutalCard>

          <BrutalCard className="p-6">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4" />
              <div className="text-sm text-muted-foreground">{t("earnings.total")}</div>
            </div>
            <div className="text-3xl font-bold">€{balance.total.toFixed(2)}</div>
          </BrutalCard>

          <BrutalCard className="p-6">
            <div className="text-sm text-muted-foreground mb-2">{t("earnings.lastPayout")}</div>
            <div className="text-lg">
              {lastPayout ? `€${parseFloat(lastPayout.amount).toFixed(2)}` : t("earnings.never")}
            </div>
            {lastPayout && (
              <div className="text-xs text-muted-foreground">
                {format(new Date(lastPayout.processed_at), "dd/MM/yyyy")}
              </div>
            )}
          </BrutalCard>
        </div>

        {/* Payout CTA */}
        {balance.available >= 50 && (
          <BrutalCard className="mb-6 p-6 bg-success/10 border-success">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div>
                <h2 className="font-heading text-lg">{t("earnings.payoutAvailable")}</h2>
                <p className="text-sm text-muted-foreground">
                  {t("earnings.payoutMessage", { amount: balance.available.toFixed(2) })}
                </p>
              </div>
              <BrutalButton onClick={() => setShowPayoutModal(true)}>
                <Banknote className="w-4 h-4 mr-2" />
                {t("earnings.requestPayout")}
              </BrutalButton>
            </div>
          </BrutalCard>
        )}

        {balance.available < 50 && balance.available > 0 && (
          <BrutalCard className="mb-6 p-6 bg-muted">
            <p className="text-sm text-center">
              {t("earnings.minimumPayout", { 
                current: balance.available.toFixed(2),
                needed: (50 - balance.available).toFixed(2)
              })}
            </p>
          </BrutalCard>
        )}

        {/* Transactions History */}
        <BrutalCard className="p-6">
          <h2 className="font-heading text-xl mb-4">{t("earnings.history")}</h2>

          {transactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-[3px] border-dark">
                    <th className="text-left py-2">{t("earnings.date")}</th>
                    <th className="text-left">{t("earnings.type")}</th>
                    <th className="text-left">{t("earnings.description")}</th>
                    <th className="text-right">{t("earnings.amount")}</th>
                    <th className="text-right">{t("earnings.balance")}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="border-b">
                      <td className="py-3">
                        {format(new Date(tx.created_at), "dd/MM/yyyy HH:mm")}
                      </td>
                      <td>{getTransactionTypeBadge(tx.transaction_type)}</td>
                      <td className="text-sm">{tx.description}</td>
                      <td className={`text-right font-bold ${
                        parseFloat(tx.amount) > 0 ? 'text-success' : 'text-destructive'
                      }`}>
                        {parseFloat(tx.amount) > 0 ? '+' : ''}€{Math.abs(parseFloat(tx.amount)).toFixed(2)}
                      </td>
                      <td className="text-right">€{parseFloat(tx.balance_after).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              {t("earnings.noTransactions")}
            </p>
          )}
        </BrutalCard>

        {/* Payout Modal */}
        {showPayoutModal && (
          <PayoutModal
            balance={balance.available}
            onClose={() => setShowPayoutModal(false)}
            onSuccess={() => {
              loadEarningsData();
              toast({ title: t("earnings.payout.requestSuccess"), variant: "success" });
            }}
          />
        )}
      </main>
      
      <Footer />
    </div>
  );
}
